import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { FiDownload, FiUsers, FiTrendingUp, FiTrendingDown, FiInfo, FiArrowRight } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { TopHoldersResult } from '../../../model/TopHoldersResult';
import { RegisterListingResult } from '../../../model/RegisterListingResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import { providers, registerAsAt } from './AllHolders';

const bands = [1, 5, 20, 50];
const collapsed = 20;

interface Entry {
    position: number; name: string; holderNumber: string; holding: number; pct: number;
    change: number | null; // null until a 30-day-old balance snapshot exists
    holderId?: number | null;
    members?: Entry[];
}

const TopHolders: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const [top, setTop] = useState<TopHoldersResult | null>(null);
    const [listing, setListing] = useState<RegisterListingResult | null>(null);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState(false);

    useAsyncEffect(async (isMounted: () => boolean) => {
        if (!stock) return;
        setTop(null);
        // the listing carries the as-at time and each holder's balance 30 days ago
        const [[err, data], [, reg]] = await Promise.all([
            to(api().getTopHolders(stock.ticker, stock.exchange)),
            to(api().getRegisterListing(stock.ticker, stock.exchange)),
        ]);
        if (!isMounted()) return;
        if (err || !data.success) { setError(err?.message || data?.message || 'Failed to fetch top holders'); return; }
        setError('');
        setTop(data);
        setListing(reg?.success ? reg : null);
    }, [stock?.id]);

    const entries = useMemo(() => {
        const primary = Math.max((listing?.securityCodes || []).indexOf(stock?.ticker || ''), 0);
        const hasHistory = !!listing?.balanceHistoricalDate;
        const byId = new Map((listing?.holders || []).map(h => [h.holderId, (h.balances?.[primary] ?? 0) - (h.balanceHistorical ?? 0)]));
        const change = (id?: number | null) => hasHistory && id ? byId.get(id) ?? null : null;
        return (top?.report?.holders || []).map((h): Entry => {
            const members = (h.groupMembers || []).map((m): Entry => ({
                position: h.position, name: (m.holderNames || []).join(' & '), holderNumber: m.holderNumber,
                holding: Number(m.holding), pct: 0, change: change(m.holderId), holderId: m.holderId,
            }));
            const groupChange = members.some(m => m.change !== null) ? members.reduce((s, m) => s + (m.change ?? 0), 0) : null;
            return {
                position: h.position, name: h.group ? h.groupName : (h.holderNames || []).join(' & '), holderNumber: h.holderNumber || '',
                holding: Number(h.holding), pct: Number(h.issuedCapitalPercentage) * 100, holderId: h.holderId,
                change: h.group ? groupChange : change(h.holderId), members: h.group ? members : undefined,
            };
        });
    }, [top, listing, stock]);

    // ranks, not rows: tied holders share a position, so the top 50 positions can be more than 50 rows
    const share = (n: number) => entries.filter(e => e.position <= n).reduce((s, e) => s + e.pct, 0);
    const last = entries.length ? entries[entries.length - 1].position : 0;
    const moves = entries.filter(e => e.change).sort((a, b) => Math.abs(b.change!) - Math.abs(a.change!)).slice(0, 3);
    const rows = expanded ? entries : entries.filter(e => e.position <= collapsed);
    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';
    const asAt = registerAsAt(listing?.asAt);
    const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toLocaleString()}`;

    const download = () => {
        const cell = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
        const lines = [['#', 'Holder', 'Holder No.', 'Holding', '% Issued', '30-day change'],
            ...entries.map(e => [e.position, e.name, e.holderNumber, e.holding, e.pct.toFixed(2), e.change ?? ''])];
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([lines.map(l => l.map(cell).join(',')).join('\r\n')], { type: 'text/csv' }));
        a.download = `${stock?.ticker}-top-holders.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const open = (e: Entry) => e.holderId && history.push(`/share-registry/holder/${e.holderId}`);
    const Row = ({ e, member }: { e: Entry; member?: boolean }) =>
        <div className={`row${member ? ' member' : ''}${e.holderId ? ' clickable' : ''}`} onClick={() => open(e)}>
            <span>{!member && <span className="rank">{e.position}</span>}</span>
            <span className="name">{member && <span className="arrow">↳ </span>}{e.name}</span>
            <span className="number">{e.holderNumber}</span>
            <span className="num">{e.holding.toLocaleString()}</span>
            <span className="num pct">{member ? '' : `${e.pct.toFixed(2)}%`}</span>
            <span className="change">{!member && <span className={`pill${e.change ? (e.change > 0 ? ' ok' : ' bad') : ''}`}>{e.change === null ? '—' : signed(e.change)}</span>}</span>
        </div>;

    return <div className="top-holders">
        <div className="page-header">
            <div>
                <h1>Share Registry</h1>
                <p>{[stock?.companyName, provider !== 'None' && provider, asAt && `Register as at ${asAt}`].filter(Boolean).join(' · ')}</p>
            </div>
            <button type="button" className="btn-primary" onClick={download} disabled={!entries.length}><FiDownload /> Download This List</button>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !top && <p className="loading">Loading top holders… (first load can take a minute while reports generate)</p>}

        {top && <div className="layout">
            <section className="card owners">
                <div className="title"><span className="icon-tile"><FiTrendingUp /></span>
                    <div><h2>Who Owns the Company</h2><p>Share of issued capital by holder band</p></div></div>
                {bands.filter(n => n <= Math.max(last, 1)).map(n => <div key={n} className="band">
                    <div className="label"><span>Top {n} holder{n > 1 ? 's' : ''}</span><strong>{share(n).toFixed(1)}%</strong></div>
                    <div className={`bar b${n}`}><span style={{ width: `${share(n)}%` }} /></div>
                </div>)}
                <div className="callout"><FiInfo /> Top 20 holders control {share(20).toFixed(1)}% of issued capital.</div>
                <div className="moves">
                    <h3>Biggest moves this month</h3>
                    {moves.map(e => <div key={e.position + e.name} className="move">
                        {e.change! > 0 ? <FiTrendingUp className="up" /> : <FiTrendingDown className="down" />}
                        <span className="name">{e.name}</span>
                        <strong className={e.change! > 0 ? 'up' : 'down'}>{signed(e.change!)}</strong>
                    </div>)}
                    {!moves.length && <p>{listing?.balanceHistoricalDate ? 'No top holder moved in the last 30 days.' : 'Available once 30 days of register history exist.'}</p>}
                </div>
            </section>

            <section className="card list">
                <div className="title"><span className="icon-tile"><FiUsers /></span>
                    <div><h2>Top {last} Holders{top.issuerName ? ` — ${top.issuerName}` : ''}</h2><p>Ranked by holding · holders with the same holding share a rank</p></div></div>
                <div className="table">
                    <div className="row head"><span>#</span><span>Holder</span><span>Holder No.</span><span className="num">Holding</span><span className="num">% Issued</span><span className="change">30-day change</span></div>
                    {rows.map(e => <React.Fragment key={`${e.position}:${e.name}`}>
                        <Row e={e} />
                        {e.members?.map(m => <Row key={m.holderNumber} e={m} member />)}
                    </React.Fragment>)}
                    {!entries.length && <p className="empty">No holders</p>}
                </div>
                {last > collapsed && <button type="button" className="link more" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Show fewer' : `Show Holders ${collapsed + 1}–${last}`} <FiArrowRight />
                </button>}
            </section>
        </div>}
    </div>;
};

export default TopHolders;
